import { isSameMonth, isSameYear } from "date-fns"
import styles from "./styles.module.scss"
import { formatDate, isDatePassed } from "@cruisen/react-shared"
import { makeAutoObservable } from "mobx"
import { useCallback, useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import ArrowBackOutline from "~icons/eva/arrow-back-outline"
import ArrowForwardOutline from "~icons/eva/arrow-forward-outline"
import classNames from "classnames"

function makeYear(
  availableMonths: Date[],
  currentYear: number,
): {
  isDateUnavailable: boolean
  date: Date
  shortenedMonthName: string
}[] {
  const months = Array.from({ length: 12 }, (_, m) => m).map((month) => {
    const date = new Date(Date.UTC(currentYear, month, 1, 0, 0, 0, 0))

    let isDateUnavailable = false

    if (availableMonths.length > 0) {
      isDateUnavailable = !availableMonths.some(
        (month) => isSameMonth(month, date) && isSameYear(month, date),
      )
    }

    if (isDatePassed(date)) {
      isDateUnavailable = true
    }

    const shortenedMonthName = formatDate(date.toISOString(), "MMM")

    return { isDateUnavailable, date, shortenedMonthName }
  })

  return months
}

class MonthPickerStore {
  currentYear: number
  selectedMonths: Date[]

  constructor(currentYear: number) {
    this.currentYear = currentYear
    this.selectedMonths = []

    makeAutoObservable(this)
  }

  setCurrentYear(year: number) {
    this.currentYear = year
  }

  setMonths(months: Date[]) {
    this.selectedMonths = months
  }

  monthDiff(dateFrom: Date, dateTo: Date) {
    return (
      dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear())
    )
  }

  toggleMonths(selectedDate: Date, availableMonths: Date[]) {
    const selectedDateTime = selectedDate.getTime()

    if (
      this.selectedMonths.length === 6 &&
      !this.selectedMonths.some((month) => month.getTime() === selectedDateTime)
    ) {
      return
    }

    if (this.selectedMonths.length === 1) {
      const firstSelectedDate = this.selectedMonths[0]
      const firstSelectedDateTime = firstSelectedDate.getTime()

      if (selectedDateTime > firstSelectedDateTime) {
        const monthDiff = this.monthDiff(firstSelectedDate, selectedDate)

        if (monthDiff >= 0 && monthDiff <= 5) {
          const dateRange = availableMonths.filter((date) => {
            const diff = this.monthDiff(firstSelectedDate, date)
            if (diff === 0 && firstSelectedDate.getMonth() !== date.getMonth()) return false
            return diff >= 0 && diff <= monthDiff
          })

          if (dateRange.length === monthDiff + 1) {
            this.selectedMonths = dateRange.splice(0, 6)
            return
          }
        }
      }
    }

    const index = this.selectedMonths.findIndex((month) => month.getTime() === selectedDateTime)

    if (index === -1) {
      this.selectedMonths.push(selectedDate)
    } else {
      this.selectedMonths.splice(index, 1)
    }
  }
}

const getYearsLimit = () => {
  const currentYear = new Date().getFullYear()
  return { min: currentYear, max: currentYear + 2 }
}

export const MonthPicker = observer(
  ({
    availableMonths,
    selectedMonths = [],
    isLoading,
    onChange,
  }: {
    availableMonths: Date[]
    selectedMonths: Date[]
    isLoading: boolean
    onChange: (dates: Date[]) => void
  }) => {
    const yearsLimit = getYearsLimit()
    const store = useMemo(() => new MonthPickerStore(yearsLimit.min), [])
    const year = makeYear(availableMonths, store.currentYear)

    const handleUpdateDates = useCallback(
      (selectedDate: Date) => store.toggleMonths(selectedDate, availableMonths),
      [],
    )

    useEffect(() => {
      onChange(store.selectedMonths)
    }, [store.selectedMonths.toString()])

    useEffect(() => {
      store.setMonths(selectedMonths)
    }, [selectedMonths.toString()])

    useEffect(() => {
      if (selectedMonths.length > 0) {
        const firstSelectedDate = selectedMonths[0]
        store.setCurrentYear(firstSelectedDate.getFullYear())
      }
    }, [])

    return (
      <div>
        <div className={styles.yearHeader}>
          <button
            className={styles.yearChangeButton}
            disabled={store.currentYear === yearsLimit.min}
            onClick={() => store.setCurrentYear(Math.max(store.currentYear - 1, yearsLimit.min))}
          >
            <ArrowBackOutline
              className={store.currentYear === yearsLimit.min ? styles.disabledIcon : ""}
            />
          </button>
          <p className={styles.yearText}>{store.currentYear}</p>
          <button
            className={styles.yearChangeButton}
            disabled={store.currentYear === yearsLimit.max}
            onClick={() => store.setCurrentYear(Math.min(store.currentYear + 1, yearsLimit.max))}
            data-testid="next-year-button"
          >
            <ArrowForwardOutline
              className={store.currentYear === yearsLimit.max ? styles.disabledIcon : ""}
            />
          </button>
        </div>
        <div className={styles.monthsGrid}>
          {year.map((month) => {
            const isSelected = store.selectedMonths.some(
              (selectedMonth) => selectedMonth.toISOString() === month.date.toISOString(),
            )

            return (
              <button
                key={month.shortenedMonthName}
                className={classNames(styles.monthButton, isSelected && styles.monthButtonSelected)}
                disabled={month.isDateUnavailable || isLoading}
                onClick={() => handleUpdateDates(month.date)}
                data-testid={`${month.shortenedMonthName.toLowerCase()}-${store.currentYear}-button`}
              >
                {month.shortenedMonthName}
              </button>
            )
          })}
        </div>
      </div>
    )
  },
)
